/* eslint-disable jsx-a11y/click-events-have-key-events */
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {PopupDropdown} from '@wandb/weave/common/components/PopupDropdown';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {fuzzyMatchRegex} from '@wandb/weave/common/util/fuzzyMatch';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {capitalizeFirst} from '@wandb/weave/core';
import {clsx} from 'clsx';
import _ from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Button,
  Input,
  Modal,
  Popup,
  StrictCheckboxProps,
  StrictInputProps,
} from 'semantic-ui-react';

import {useUpdateGalleryMutation} from '../../generated/graphql';
import {PROJECT_ACCESSES_QUERY} from '../../state/graphql/gallery';
import {useReportPageQueryResult} from '../../state/graphql/reportPageQuery';
import {useApolloClient, useSelector} from '../../state/hooks';
import {ReportObjSchema} from '../../state/views/report/types';
import {
  DEFAULT_LANGUAGE,
  DISCUSSION_CATEGORY_ID,
  GallerySpec,
  getAncestorTagIDSetForReport,
  Language,
  LANGUAGES,
  ReportIDWithTagIDs,
  ReportMetadata,
  TagV2,
  triggerPrerenderRecacheForTags,
  useGallerySpec,
  useReportMetadata,
} from '../../util/gallery';
import {accessIsPrivate, AccessOptions} from '../../util/permissions';
import {parseReportURL} from '../../util/report';
import {useReportProjects} from '../../util/report/useReportProjects';
import {ReportAuthor} from '../../util/section';
import {reportView} from '../../util/urls';
import {Post} from '../GalleryPage';
import {useLoadedReportContext} from './LoadedReportContext';
import * as S from './ReportAdminBar.styles';
import {useReportPageContext} from './ReportPageContext';

const ADMIN_COLOR = '#884BF2';

export const ReportAdminBar = () => {
  const {entityName, projectName} = useReportPageContext();
  const {view} = useLoadedReportContext();

  const isGalleryPost = !projectName;

  const {
    loading: galleryLoading,
    viewID: galleryViewID,
    gallerySpec,
    refetchGallerySpec,
  } = useGallerySpec();

  const originalReportID = gallerySpec.reportIDsWithTagV2IDs.find(
    ({id}) => id === view.id
  )?.isTranslationOf;

  const reportIDs = useMemo(() => {
    if (view.id == null) {
      throw new Error(`view with null ID`);
    }
    if (originalReportID == null) {
      return [view.id];
    }
    return [view.id, originalReportID];
  }, [view.id, originalReportID]);

  const {loading: reportMetadataLoading, reportMetadatas} =
    useReportMetadata(reportIDs);

  const reportPageQuery = useReportPageQueryResult({
    variables: {
      entityName,
      projectName: projectName ?? '',
    },
  });

  const reportMetadata = reportMetadatas?.find(({id}) => id === view.id);
  const originalReportMetadata =
    reportMetadatas?.find(({id}) => id === originalReportID) ?? null;

  if (
    galleryLoading ||
    galleryViewID == null ||
    reportMetadataLoading ||
    reportMetadata == null ||
    reportPageQuery.loading ||
    (!isGalleryPost && reportPageQuery.data == null)
  ) {
    return null;
  }

  return (
    <ReportAdminBarLoaded
      galleryViewID={galleryViewID}
      gallerySpec={gallerySpec}
      refetchGallerySpec={refetchGallerySpec}
      reportMetadata={reportMetadata}
      originalReportMetadata={originalReportMetadata}
      projectAccess={
        isGalleryPost || reportPageQuery.data == null
          ? 'USER_READ'
          : reportPageQuery.data.project.access
      }
    />
  );
};

type ReportAdminBarLoadedProps = {
  galleryViewID: string;
  gallerySpec: GallerySpec;
  refetchGallerySpec: () => Promise<GallerySpec>;
  reportMetadata: ReportMetadata;
  originalReportMetadata: ReportMetadata | null;
  projectAccess: AccessOptions;
};

export const ReportAdminBarLoaded: React.FC<
  ReportAdminBarLoadedProps
> = props => {
  const {
    galleryViewID,
    gallerySpec,
    refetchGallerySpec,
    reportMetadata,
    originalReportMetadata,
    projectAccess,
  } = props;
  const {reportViewRef, view} = useLoadedReportContext();

  const viewPart = useSelector(state =>
    view?.partRef != null
      ? state.views.parts[view.partRef.type as ReportObjSchema['type']][
          view.partRef.id
        ]
      : null
  );

  const authors: ReportAuthor[] =
    viewPart?.authors != null && viewPart.authors.length > 0
      ? viewPart.authors
      : [{username: view.user.username, name: view.user.name}];

  const [reportCardDraft, setReportCardDraft] = useState<ReportMetadata>({
    ...reportMetadata,
    authors: reportMetadata.authors ?? authors,
  });
  const [tagByID, tagByName]: [Map<string, TagV2>, Map<string, TagV2>] =
    useMemo(
      () => [
        new Map(gallerySpec.tagsV2.map(t => [t.id, t])),
        new Map(gallerySpec.tagsV2.map(t => [t.name, t])),
      ],
      [gallerySpec.tagsV2]
    );
  const setTags = useCallback(
    (newTags: string[]) => {
      setReportCardDraft(prev => ({
        ...prev,
        tags: newTags.map(t => tagByName.get(t)).filter(isNotNullOrUndefined),
      }));
    },
    [tagByName]
  );

  const categoryNameSet = useMemo(
    () => new Set(gallerySpec.categories.map(t => t.name)),
    [gallerySpec.categories]
  );
  const tagIsCategory = useCallback(
    (tagName: string) => categoryNameSet.has(tagName),
    [categoryNameSet]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [party, setParty] = useState(false);
  const [addingToGallery, setAddingToGallery] = useState(false);
  const [removingFromGallery, setRemovingFromGallery] = useState(false);

  const [primaryTagID, setPrimaryTagID] = useState<string | undefined>(
    reportMetadata.primaryTagID
  );
  useEffect(() => {
    if (primaryTagID == null) {
      return;
    }
    const primaryTagExists =
      reportCardDraft.tags?.some(t => t.id === primaryTagID) ?? false;
    if (!primaryTagExists) {
      setPrimaryTagID(undefined);
    }
  }, [primaryTagID, reportCardDraft.tags]);
  const primaryTagName = useMemo(() => {
    if (primaryTagID == null) {
      return null;
    }
    return tagByID.get(primaryTagID)?.name ?? null;
  }, [tagByID, primaryTagID]);
  const primaryTagOptions = useMemo(() => {
    const opts =
      reportCardDraft.tags?.map(t => ({
        text: t.name,
        onClick: () => setPrimaryTagID(t.id),
      })) ?? [];
    return [
      {text: `Clear primary tag`, onClick: () => setPrimaryTagID(undefined)},
      ...opts,
    ];
  }, [reportCardDraft.tags]);

  const [onlyShowOnTagIDs, setOnlyShowOnTagIDs] = useState<Set<string>>(
    new Set(reportMetadata.onlyShowOnTagIDs)
  );
  useEffect(() => {
    const validTags = [...onlyShowOnTagIDs].filter(tagID =>
      reportCardDraft.tags?.some(t => t.id === tagID)
    );
    if (validTags.length !== onlyShowOnTagIDs.size) {
      setOnlyShowOnTagIDs(new Set(validTags));
    }
  }, [onlyShowOnTagIDs, reportCardDraft.tags]);

  const [currentTagIDs, currentTagNames] = useMemo(
    () => [
      reportCardDraft.tags?.map(t => t.id) ?? [],
      reportCardDraft.tags?.map(t => t.name) ?? [],
    ],
    [reportCardDraft.tags]
  );

  const [language, setLanguage] = useState<Language>(reportMetadata.language);
  const [linkToOriginal, setLinkToOriginal] = useState(
    originalReportMetadata != null
      ? reportView({
          entityName:
            originalReportMetadata.project?.entityName ??
            originalReportMetadata.entityName,
          projectName: originalReportMetadata.project?.name ?? '',
          reportID: originalReportMetadata.id,
          reportName: originalReportMetadata.displayName,
          isDiscussion:
            originalReportMetadata.tags?.some(
              ({id}) => id === DISCUSSION_CATEGORY_ID
            ) ?? false,
        })
      : ''
  );

  const languageOptions = useMemo(
    () => LANGUAGES.map(o => ({text: o, onClick: () => setLanguage(o)})),
    []
  );
  const isTranslation = language !== DEFAULT_LANGUAGE;

  const [publishTime, setPublishTime] = React.useState('');
  useEffect(() => {
    if (publishTime === '') {
      setReportCardDraft(prev => ({
        ...prev,
        addedAt: reportMetadata.addedAt,
      }));
      return;
    }
    const timestamp = Date.parse(publishTime);
    if (isNaN(timestamp)) {
      setReportCardDraft(prev => ({
        ...prev,
        addedAt: reportMetadata.addedAt,
      }));
      return;
    }
    setReportCardDraft(prev => ({
      ...prev,
      addedAt: new Date(timestamp).toJSON(),
    }));
  }, [publishTime, reportMetadata]);

  const PARTY_DELAY = 40;
  const PARTY_DURATION = 2200;
  const throwParty = () =>
    setTimeout(() => {
      setParty(true);
      setTimeout(() => {
        setParty(false);
      }, PARTY_DURATION);
    }, PARTY_DELAY);

  const openModal = () => {
    setModalOpen(true);

    // HAX: No good way to set a style on Semantic's dimmer which wraps the modal.
    setTimeout(() => {
      const dimmer = document.querySelector('.ui.dimmer') as HTMLDivElement;
      dimmer.style.overflow = 'auto';
    });
  };

  const [updateGallery] = useUpdateGalleryMutation();

  const inGallery = gallerySpec.reportIDsWithTagV2IDs.some(
    ({id}) => view.id === id
  );

  const client = useApolloClient();
  const projectSpecifiers = useReportProjects(reportViewRef);
  const hasPrivateRunSetRef = useRef(false);
  useEffect(() => {
    const projectAccessPromises = projectSpecifiers.map(ps =>
      client.query({
        query: PROJECT_ACCESSES_QUERY,
        variables: {name: ps.projectName, entityName: ps.entityName},
      })
    );
    (async () => {
      try {
        const results = await Promise.all(projectAccessPromises);
        hasPrivateRunSetRef.current = results.some(r => {
          const acc = r?.data?.project?.access;
          if (acc == null) {
            return true;
          }
          return accessIsPrivate(acc);
        });
      } catch (err) {
        console.error('error loading project accesses', err);
        console.log('project specifiers', projectSpecifiers);
      }
    })();
  }, [client, projectSpecifiers]);

  const addToGallery = async () => {
    if (hasPrivateRunSetRef.current) {
      alert(
        `This report contains private run set(s). You must publish it to add it to the gallery.`
      );
      return;
    }

    const newEntry: ReportIDWithTagIDs = {
      id: reportCardDraft.id,
      tagIDs: currentTagIDs,
      primaryTagID,
      onlyShowOnTagIDs: [...onlyShowOnTagIDs],
      authors: reportCardDraft.authors ?? authors,
      addedAt: reportCardDraft.addedAt ?? new Date().toJSON(),
      language,
      pending: reportCardDraft.pending,
      announcement: reportCardDraft.announcement,
    };

    if (isTranslation) {
      if (linkToOriginal === '') {
        alert(
          `All translation reports must include a link to the original English report.`
        );
        return;
      }
      const {reportId} = parseReportURL(linkToOriginal);
      if (reportId == null) {
        alert(`Link to original English report is invalid.`);
        return;
      }
      newEntry.isTranslationOf = reportId;
    }

    setAddingToGallery(true);
    try {
      const upToDateGallerySpec = await refetchGallerySpec();
      const updatedInGallery = upToDateGallerySpec.reportIDsWithTagV2IDs.some(
        ({id}) => id === view.id
      );
      if (newEntry.isTranslationOf != null) {
        const originalReportExists =
          upToDateGallerySpec.reportIDsWithTagV2IDs.some(
            ({id}) => id === newEntry.isTranslationOf
          );
        if (!originalReportExists) {
          alert(`Original English report does not exist in the gallery.`);
          return;
        }
      }

      let newReportList: ReportIDWithTagIDs[];
      if (updatedInGallery) {
        const reportIdx = upToDateGallerySpec.reportIDsWithTagV2IDs.findIndex(
          r => view.id === r.id
        );
        newReportList = _.cloneDeep(upToDateGallerySpec.reportIDsWithTagV2IDs);
        newReportList = [
          ...newReportList.slice(0, reportIdx),
          newEntry,
          ...newReportList.slice(reportIdx + 1).filter(r => view.id !== r.id),
        ];
      } else {
        newReportList = [
          newEntry,
          ...upToDateGallerySpec.reportIDsWithTagV2IDs,
        ];
      }

      let tagsToUpdateSet: Set<string>;
      if (updatedInGallery) {
        const originalReport: ReportIDWithTagIDs | undefined =
          upToDateGallerySpec.reportIDsWithTagV2IDs.find(r => view.id === r.id);
        const originalTagSet: Set<string> =
          originalReport != null
            ? getAncestorTagIDSetForReport(tagByID, originalReport)
            : new Set();
        const newTagSet = getAncestorTagIDSetForReport(tagByID, newEntry);
        tagsToUpdateSet = symmetricSetDifference(originalTagSet, newTagSet);
      } else {
        tagsToUpdateSet = getAncestorTagIDSetForReport(tagByID, newEntry);
      }

      const updateTime = new Date().toJSON();
      const tagsToUpdate = upToDateGallerySpec.tagsV2.filter(t =>
        tagsToUpdateSet.has(t.id)
      );
      tagsToUpdate.forEach(t => (t.updatedAt = updateTime));

      await updateGallery({
        variables: {
          id: galleryViewID,
          spec: JSON.stringify({
            ...upToDateGallerySpec,
            reportIDsWithTagV2IDs: newReportList,
          }),
        },
      });

      await triggerPrerenderRecacheForTags(tagsToUpdate);

      throwParty();
    } catch {
      alert('Failed to save');
    } finally {
      setAddingToGallery(false);
    }
  };

  const removeFromGallery = async () => {
    setRemovingFromGallery(true);
    try {
      const upToDateGallerySpec = await refetchGallerySpec();

      const newReportList = _.filter(
        upToDateGallerySpec.reportIDsWithTagV2IDs,
        r => view.id !== r.id
      );

      const updateTime = new Date().toJSON();
      const removedReport = upToDateGallerySpec.reportIDsWithTagV2IDs.find(
        r => view.id === r.id
      );
      const tagsToUpdateSet =
        removedReport != null
          ? getAncestorTagIDSetForReport(tagByID, removedReport)
          : new Set();
      const tagsToUpdate = upToDateGallerySpec.tagsV2.filter(t =>
        tagsToUpdateSet.has(t.id)
      );
      tagsToUpdate.forEach(t => (t.updatedAt = updateTime));

      await updateGallery({
        variables: {
          id: galleryViewID,
          spec: JSON.stringify({
            ...upToDateGallerySpec,
            reportIDsWithTagV2IDs: newReportList,
          }),
        },
      });

      await triggerPrerenderRecacheForTags(tagsToUpdate);

      throwParty();
    } catch {
      alert('Failed to remove');
    } finally {
      setRemovingFromGallery(false);
    }
  };

  const allTagNames: string[] = useMemo(
    () => gallerySpec.tagsV2.map(t => t.name),
    [gallerySpec]
  );

  const featureable =
    projectAccess === 'USER_READ' || projectAccess === 'USER_WRITE';

  return (
    <div
      data-test="add-to-gallery-admin-bar"
      className="hide-on-print"
      style={{
        position: 'relative',
        width: '100%',
        height: 52,
        backgroundColor: globals.galleryAdminPurple,
        display: 'flex',
        alignItems: 'center',
      }}>
      <Popup
        basic
        open={popupOpen}
        onOpen={() => {
          setPopupOpen(true);
        }}
        onClose={() => {
          setPopupOpen(false);
        }}
        trigger={
          <LegacyWBIcon
            data-test="add-to-gallery-trigger"
            style={{
              fontSize: '2.0em',
              cursor: 'pointer',
              color: ADMIN_COLOR,
              position: 'absolute',
              right: 15,
            }}
            name="bowtie"
          />
        }
        on="click">
        <Popup.Header
          style={{
            color: ADMIN_COLOR,
            textTransform: 'uppercase',
            alignItems: 'center',
            display: 'flex',
          }}>
          <LegacyWBIcon
            style={{
              fontSize: '1.15em',
            }}
            name="bowtie"
          />
          <span style={{fontSize: 12}}>Admin Only</span>
        </Popup.Header>
        <div
          data-test="add-to-gallery-button"
          onClick={() => {
            setPopupOpen(false);
            openModal();
          }}
          style={{cursor: 'pointer'}}>
          Add report to gallery
        </div>
      </Popup>

      <Modal
        open={modalOpen}
        onOpen={() => {
          openModal();
        }}
        onClose={() => setModalOpen(false)}>
        <Modal.Header
          style={{
            color: ADMIN_COLOR,
            display: 'flex',
            alignItems: 'center',
          }}>
          <LegacyWBIcon style={{color: ADMIN_COLOR}} size="big" name="bowtie" />
          Admin: Add to gallery
          <span
            style={{
              transition: 'all 0.2s',
              opacity: party ? 1 : 0,
              position: 'absolute',
              right: 20,
            }}>
            Successful update!
            <span aria-label="cheers" role="img">
              🥂
            </span>
            <span aria-label="growth" role="img">
              🌱
            </span>
          </span>
        </Modal.Header>
        {featureable ? (
          <>
            <Modal.Content>
              Here's what the report will look like in the gallery. Add tags
              from the list to categorize this report.
              <div style={{maxWidth: 600, margin: 'auto'}}>
                <Post {...reportCardDraft} tagIsCategory={tagIsCategory} />
              </div>
              <TagPicker
                availableTags={allTagNames}
                setTags={setTags}
                currentTags={currentTagNames}
              />
              <S.Label>Primary Tag</S.Label>
              <PopupDropdown
                options={primaryTagOptions}
                offset={`0px, -16px`}
                trigger={
                  <S.Dropdown>
                    {primaryTagName ?? `Select primary tag`}{' '}
                    <S.DropdownIcon name="chevron-down" />
                  </S.Dropdown>
                }
              />
              <S.Label>Show Only on Tag Pages</S.Label>
              {currentTagIDs.map(tagID => {
                const tagName = tagByID.get(tagID)?.name;
                if (!tagName) {
                  return null;
                }
                return (
                  <S.CheckboxRow key={tagID}>
                    <S.CheckboxRowInner
                      onClick={() =>
                        setOnlyShowOnTagIDs(prev => {
                          const next = new Set(prev);
                          if (prev.has(tagID)) {
                            next.delete(tagID);
                          } else {
                            next.add(tagID);
                          }
                          return next;
                        })
                      }>
                      <S.Checkbox checked={onlyShowOnTagIDs.has(tagID)} />
                      <S.CheckboxLabel>{tagName}</S.CheckboxLabel>
                    </S.CheckboxRowInner>
                  </S.CheckboxRow>
                );
              })}
              <S.Label>Language</S.Label>
              <PopupDropdown
                options={languageOptions}
                offset={`0px, -16px`}
                trigger={
                  <S.Dropdown>
                    {language} <S.DropdownIcon name="chevron-down" />
                  </S.Dropdown>
                }
              />
              {isTranslation && (
                <>
                  <S.Label>
                    Please paste the full link to the English report
                  </S.Label>
                  <S.Input
                    $wide
                    placeholder="https://wandb.ai/armandpl/furuta/reports/Training-Reproducible-Robots-with-W-B--VmlldzoxMTY5NTM5"
                    value={linkToOriginal}
                    onChange={
                      ((e, {value}) =>
                        setLinkToOriginal(
                          value
                        )) as StrictInputProps['onChange']
                    }
                  />
                </>
              )}
              <S.Label>Publish Time</S.Label>
              <S.Input
                placeholder="1/1/2021 09:00 AM"
                value={publishTime}
                onChange={
                  ((e, {value}) =>
                    setPublishTime(value)) as StrictInputProps['onChange']
                }
              />
              <S.Label>Announcement</S.Label>
              <S.Checkbox
                checked={!!reportCardDraft.announcement}
                onChange={
                  ((e, {checked}) =>
                    setReportCardDraft(prev => ({
                      ...prev,
                      announcement: checked ? true : undefined,
                    }))) as StrictCheckboxProps['onChange']
                }
              />
              <S.Label>Approved</S.Label>
              <S.Checkbox
                checked={!reportCardDraft.pending}
                onChange={
                  ((e, {checked}) =>
                    setReportCardDraft(prev => ({
                      ...prev,
                      pending: checked ? undefined : true,
                    }))) as StrictCheckboxProps['onChange']
                }
              />
            </Modal.Content>
            <Modal.Actions>
              <Button
                disabled={!inGallery || removingFromGallery || addingToGallery}
                loading={removingFromGallery}
                style={{
                  backgroundColor: inGallery ? ADMIN_COLOR : null,
                  color: inGallery ? 'white' : null,
                  position: 'absolute',
                  left: 10,
                }}
                onClick={() => {
                  removeFromGallery();
                }}>
                Remove from gallery
              </Button>
              <Button
                // style={{backgroundColor: ADMIN_COLOR, color: 'white'}}
                onClick={() => {
                  setModalOpen(false);
                }}>
                Cancel
              </Button>
              <Button
                data-test="add-to-gallery-submit"
                disabled={addingToGallery || removingFromGallery}
                loading={addingToGallery}
                style={{backgroundColor: ADMIN_COLOR, color: 'white'}}
                onClick={addToGallery}>
                {inGallery ? 'Update gallery card' : 'Add to gallery'}
              </Button>
            </Modal.Actions>
          </>
        ) : (
          <>
            <Modal.Content>
              This project is private — make it public to add this report to the
              gallery
            </Modal.Content>

            <Modal.Actions>
              <Button
                style={{backgroundColor: ADMIN_COLOR, color: 'white'}}
                onClick={() => {
                  setModalOpen(false);
                }}>
                Okay
              </Button>
            </Modal.Actions>
          </>
        )}
      </Modal>
    </div>
  );
};

const TagPicker = (props: {
  setTags: (tags: string[]) => void;
  currentTags: string[];
  availableTags: string[];
}) => {
  const {currentTags, availableTags} = props;
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<Input | null>(null);

  const matchedTags = availableTags.filter(t =>
    t.match(fuzzyMatchRegex(query))
  );

  const setTags = (tags: string[]) => {
    props.setTags(_.uniq(tags));
  };

  return (
    <div
      style={{
        width: '100%',
        marginTop: 10,
      }}
      className={clsx('report-header-edit__author-adder', 'active')}>
      <div
        onClick={() => {
          if (inputRef.current != null) {
            inputRef.current.focus();
          }
        }}
        className="author-adder-fake-input">
        {currentTags.map(tag => (
          <div key={tag} className="author-adder-added">
            {tag}
            <LegacyWBIcon
              name="close"
              className="author-adder-remove"
              onClick={() => {
                setTags(currentTags.filter(v => v !== tag));
              }}
            />
          </div>
        ))}
        <Input
          ref={el => (inputRef.current = el)}
          value={query}
          onChange={(e, {value}) => setQuery(value)}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setShowDropdown(false)}
          placeholder={'Select Tags'}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
              setTags(props.currentTags.concat([capitalizeFirst(query)]));
              setQuery('');
            }
          }}
        />
      </div>
      <div
        style={{zIndex: 2}}
        className={clsx('author-adder-dropdown', {
          active: showDropdown && availableTags.length > 0,
        })}>
        {matchedTags.map(tag => (
          <div
            key={tag}
            className="author-adder-dropdown-item"
            onClick={() => {
              setTags(currentTags.concat([tag]));
              setQuery('');
            }}>
            {tag}
          </div>
        ))}
      </div>
    </div>
  );
};

function symmetricSetDifference<T>(setA: Set<T>, setB: Set<T>) {
  const difference = new Set(setA);
  for (const elem of setB) {
    if (difference.has(elem)) {
      difference.delete(elem);
    } else {
      difference.add(elem);
    }
  }
  return difference;
}
